import { ComponentPortal, ComponentType, DomPortalOutlet } from '@angular/cdk/portal';
import {
  ApplicationRef,
  Compiler,
  ComponentRef,
  Injectable,
  InjectionToken,
  Injector,
  NgModuleFactory,
  Type,
} from '@angular/core';

import { DynamicContainerComponent } from '../components/dynamic-container/dynamic-container.component';
import { modules } from '../models/dynamic-items.registry';
import { ModalConfiguration } from '../models/modal-configuration.model';

export const DYNAMIC_COMPONENT = new InjectionToken<any>('DYNAMIC_COMPONENT');

@Injectable()
export class DynamicLoaderService {
  private containerId = 0;
  private registry: { [key: string]: ComponentRef<any> } = {};

  constructor(
    private injector: Injector,
    private compiler: Compiler,
    private appRef: ApplicationRef
  ) {}

  open<C extends object>(
    moduleOrComp: ComponentType<C> | string,
    config: ModalConfiguration,
    data: any
  ): Promise<DynamicContainerComponent<C>> {
    const portalHost = new DomPortalOutlet(document.body, this.appRef, this.injector);
    if (this.isComponent<C>(moduleOrComp)) {
      return this.instantiateComponent<C>(
        moduleOrComp as ComponentType<C>,
        portalHost,
        this.injector,
        config,
        data
      );
    } else {
      return this.instantiateLazyComponent<C>(
        moduleOrComp as string,
        portalHost,
        this.injector,
        config,
        data
      );
    }
  }

  instantiateLazyComponent<C>(
    path: string,
    portalHost: DomPortalOutlet,
    injector: Injector,
    config: ModalConfiguration,
    data: any
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const module = modules.find(r => r.path === path);
      if (!module) {
        throw new Error(
          `Unrecognized module "${path}". Make sure it is registered in the component registry`
        );
      }
      module
        .loadChildren()
        .then(moduleOrFactory => this.getCompiledModule(moduleOrFactory))
        .then(moduleFactory => {
          const containerComponent = this.createContainerComponent<C>(
            moduleFactory,
            portalHost,
            injector,
            config,
            data
          );
          resolve(containerComponent.instance);
        })
        .catch(err => {
          console.error('error loading module', err);
          reject(err);
        });
    });
  }

  removeComponentFromBody(refId: number): void {
    if (this.registry[refId]) {
      this.appRef.detachView(this.registry[refId].hostView);
      this.registry[refId].destroy();
      delete this.registry[refId];
    } else {
      console.warn(`Container with id '${refId}' doesn't exist in the registry`);
    }
  }

  removeAllDynamicComponents(): void {
    Object.entries(this.registry).forEach(([key, value]) => {
      this.appRef.detachView(value.hostView);
      value.destroy();
      delete this.registry[key];
    });
  }

  private isComponent<C>(moduleOrComp: ComponentType<C> | string): boolean {
    return moduleOrComp instanceof Type;
  }

  private instantiateComponent<C>(
    moduleOrComp: ComponentType<C>,
    portalHost: DomPortalOutlet,
    injector: Injector,
    config: ModalConfiguration,
    data: any
  ): Promise<any> {
    return new Promise(resolve => {
      const containerRef = this.createDomElement<C>(
        moduleOrComp,
        portalHost,
        injector,
        config,
        data
      );
      resolve(containerRef.instance);
    });
  }

  private getCompiledModule(
    moduleOrFactory: NgModuleFactory<any> | Type<any>
  ): Promise<NgModuleFactory<any>> {
    if (moduleOrFactory instanceof NgModuleFactory) {
      return new Promise(resolve => resolve(moduleOrFactory));
    } else {
      return this.compiler.compileModuleAsync(moduleOrFactory);
    }
  }

  private createContainerComponent<C>(
    moduleFactory: NgModuleFactory<any>,
    portalHost: DomPortalOutlet,
    injector: Injector,
    config: ModalConfiguration,
    data: any
  ): ComponentRef<C> {
    const moduleRef = moduleFactory.create(injector);
    const dynamicComponentType = moduleRef.injector.get(DYNAMIC_COMPONENT);
    return this.createDomElement<C>(dynamicComponentType, portalHost, injector, config, data);
  }

  private createDomElement<C>(
    dynamicComponentType: ComponentType<C>,
    portalHost: DomPortalOutlet,
    injector: Injector,
    config: ModalConfiguration,
    data: any
  ): ComponentRef<any> {
    this.containerId++;

    const modalComponent = new ComponentPortal(DynamicContainerComponent, undefined, injector);
    const modalComponentRef = portalHost.attach(modalComponent);
    const modalInstance = modalComponentRef.instance;
    modalInstance.modalConfiguration = config;

    this.registry[this.containerId] = modalComponentRef;

    modalInstance.dynamicLoaderService = this;
    modalInstance.containerId = this.containerId;

    const childComponent = new ComponentPortal(dynamicComponentType, undefined, injector);

    modalInstance.attachChildComponent(childComponent, data);

    return modalComponentRef;
  }
}
